import { useMemo, useState } from "react"
import { toast } from "react-toastify"
import { Badge, Button } from "reactstrap"

import ThemeModal from "components/Common/ThemeModal"
import FileViewerModal from "components/Common/FileViewerModal"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { downloadVehicleLcDocumentAcknowledgementPdf } from "api/vehicleLcDocumentApi"
import {
  useVehicleLcDocuments,
} from "hooks/useVehicleLcDocuments"

const vehicleLabel = vehicle =>
  [vehicle?.stock_id, vehicle?.full_chassis || vehicle?.chassis_number]
    .filter(Boolean)
    .join(" - ")

const acknowledgementReference = group =>
  group?.lc_document_acknowledgement?.reference_number || "-"

const vehicleName = vehicle =>
  [vehicle?.brand?.name, vehicle?.model?.name, vehicle?.year?.name]
    .filter(Boolean)
    .join(" ")

const vehicleDescription = vehicle =>
  [
    vehicleName(vehicle),
    vehicle?.exterior_color?.name ? `Color: ${vehicle.exterior_color.name}` : "",
    vehicle?.full_chassis || vehicle?.chassis_number
      ? `Chassis No.: ${vehicle.full_chassis || vehicle.chassis_number}`
      : "",
  ]
    .filter(Boolean)
    .join(", ")

const formatPdfLikeDate = value =>
  new Date(value).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })

const documentNameWithAttested = document => {
  const name = document?.lc_document?.name || document?.lcDocument?.name
  return name && document?.is_attested ? `${name} (Attested)` : name
}

const documentRemarks = document =>
  [
    documentNameWithAttested(document),
    document?.document_date ? formatPdfLikeDate(document.document_date) : "",
    document?.remarks,
  ]
    .filter(Boolean)
    .join(" ")

const acknowledgementAttachments = group =>
  Array.isArray(group?.lc_document_acknowledgement?.attachments)
    ? group.lc_document_acknowledgement.attachments
    : []

const isPdfAttachment = attachment => {
  const mime = String(attachment?.mime_type || "").toLowerCase()
  if (mime === "application/pdf") return true

  return String(
    attachment?.original_name || attachment?.filename || ""
  )
    .toLowerCase()
    .endsWith(".pdf")
}

const acknowledgementDocumentKey = document =>
  [
    document?.lc_document_id,
    document?.document_date ? String(document.document_date).slice(0, 10) : "",
    document?.remarks,
  ].join("|")

const expandAttestedDocuments = documents => {
  const regularKeys = new Set(
    documents
      .filter(document => !document.is_attested)
      .map(document => acknowledgementDocumentKey(document))
  )

  return documents.flatMap(document => {
    if (
      !document.is_attested ||
      regularKeys.has(acknowledgementDocumentKey(document))
    ) {
      return [document]
    }

    return [
      {
        ...document,
        id: `regular-copy-${document.id}`,
        is_attested: false,
        isSynthetic: true,
      },
      document,
    ]
  })
}

const formattedToday = () =>
  new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })

const VehicleLcDocumentDetailsModal = ({
  isOpen,
  toggle,
  group,
}) => {
  const [pdfLoading, setPdfLoading] = useState(false)
  const [viewerFile, setViewerFile] = useState(null)

  const vehicleId = group?.id

  const {
    data: responseData,
    isLoading,
    isRefetching,
  } = useVehicleLcDocuments(
    { vehicle_stock_id: vehicleId, all: true },
    isOpen && !!vehicleId
  )

  const documents = responseData?.data || []
  const attachments = acknowledgementAttachments(group)
  const displayDocuments = useMemo(
    () => expandAttestedDocuments(documents),
    [documents]
  )

  const handlePrint = () => {
    if (!vehicleId) return
    downloadVehicleLcDocumentAcknowledgementPdf({
      vehicleStockId: vehicleId,
      onStart: () => setPdfLoading(true),
      onEnd: () => setPdfLoading(false),
    }).catch(() => toast.error("Failed to open acknowledgement PDF."))
  }

  const handleViewAttachment = attachment => {
    if (!attachment?.url) {
      toast.error("Attachment preview is unavailable.")
      return
    }

    setViewerFile({
      url: attachment.url,
      name:
        attachment.original_name ||
        attachment.filename ||
        "Attachment",
    })
  }

  const footer = (
    <div className="d-flex justify-content-between w-100">
      <div>
        <Badge className="badge-soft-info me-1">
          {displayDocuments.length} Docs
        </Badge>
        <Badge className="badge-soft-success">
          {documents.filter(item => item.is_attested).length} Attested
        </Badge>
      </div>
      <div>
        <CanView access={[ACCESS.VEHICLE_LC_DOCUMENT.PRINT_ACKNOWLEDGEMENT]}>
          <Button
            color="danger"
            onClick={handlePrint}
            className="ms-1"
            disabled={pdfLoading}
          >
            <i className={`mdi ${pdfLoading ? "mdi-loading mdi-spin" : "mdi-file-pdf-box"} me-1`} />
            Print
          </Button>
        </CanView>
        <Button color="light" onClick={toggle} className="ms-1">
          Close
        </Button>
      </div>
    </div>
  )

  return (
    <>
      <ThemeModal
        isOpen={isOpen}
        toggle={toggle}
        title={`Vehicle LC Documents - ${vehicleLabel(group) || "Vehicle"} - ${acknowledgementReference(group)}`}
        size="xl"
        footer={footer}
      >
        <div className="border rounded bg-white p-4">
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <div className="fw-bold fs-5 text-uppercase">Aurobil Cars</div>
              <div className="text-muted small">
                Original LC document acknowledgement preview
              </div>
            </div>
            <div className="text-end">
              <div className="fw-bold fs-5 text-danger">LC DOCUMENTS</div>
              <div className="small mt-2">
                <div>
                  <span className="fw-semibold">Reference No.</span>{" "}
                  {acknowledgementReference(group)}
                </div>
                <div>
                  <span className="fw-semibold">Date</span> {formattedToday()}
                </div>
              </div>
            </div>
          </div>

          <h5 className="text-center text-uppercase fw-bold mb-3">
            <span style={{ borderBottom: "1.5px dashed #dee2e6" }}>
              Original LC Documents Acknowledgement
            </span>
          </h5>

          <p className="lh-lg text-justify">
            I hereby declare that I have received the following original LC
            documents from Aurobil Cars for{" "}
            <mark className="fw-bold px-1">
              {vehicleDescription(group) || "the described vehicle"}
            </mark>
            .
          </p>

          {isLoading || isRefetching ? (
            <div className="text-center text-muted py-5">Loading documents...</div>
          ) : displayDocuments.length ? (
            <ol className="ps-4 mb-5">
              {displayDocuments.map(document => (
                <li key={document.id} className="mb-2">
                  <div>
                    <span className="lh-lg">{documentRemarks(document) || "-"}</span>
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <div className="text-center text-muted py-5">
              No LC documents recorded.
            </div>
          )}

          {attachments.length > 0 && (
            <div className="border-top pt-3 mb-4">
              <div className="fw-semibold mb-2">Attachment PDFs</div>
              <div className="d-flex flex-wrap gap-2">
                {attachments.map((attachment, index) => (
                  <Button
                    key={attachment.id || attachment.url || index}
                    type="button"
                    color="link"
                    className="badge badge-soft-danger border-0 text-decoration-none"
                    onClick={() => handleViewAttachment(attachment)}
                    title={
                      attachment.original_name ||
                      attachment.filename ||
                      "Attachment"
                    }
                  >
                    {isPdfAttachment(attachment) ? (
                      <>
                        <i className="mdi mdi-file-pdf-box me-1" />
                        {attachment.original_name ||
                          attachment.filename ||
                          `PDF ${index + 1}`}
                      </>
                    ) : (
                      <>
                        <img
                          src={attachment.url}
                          alt={attachment.original_name || "image"}
                          style={{
                            width: 40,
                            height: 40,
                            objectFit: "cover",
                            borderRadius: 4,
                          }}
                          className="me-1"
                        />
                        {attachment.original_name ||
                          attachment.filename ||
                          `Image ${index + 1}`}
                      </>
                    )}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div className="d-flex justify-content-between mt-5 pt-4">
            <div
              className="text-center w-25 pt-2"
              style={{ borderTop: "1.5px dashed #dee2e6" }}
            >
              <div>Authorized Signature</div>
              <div className="fw-semibold">Aurobil Cars</div>
            </div>
            <div
              className="text-center w-25 pt-2"
              style={{ borderTop: "1.5px dashed #dee2e6" }}
            >
              Customer / Receiver
            </div>
          </div>
        </div>
      </ThemeModal>
      <FileViewerModal
        isOpen={!!viewerFile}
        onClose={() => setViewerFile(null)}
        fileUrl={viewerFile?.url || ""}
        fileName={viewerFile?.name || "PDF attachment"}
      />
    </>
  )
}

export default VehicleLcDocumentDetailsModal
